import { useTranslation } from 'react-i18next';

import EmptyState from '@/components/ui/EmptyState';
import SectionHeader from '@/components/ui/SectionHeader';
import InstructButton from '@/components/production/tiles/InstructButton';
import { useProductionContext } from '@/context/ProductionContext';
import type { AssetInfo } from '@/hooks/useProduction';

function Group({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-faint">
        {title}
        {hint && <span className="ml-2 font-normal normal-case tracking-normal text-faint/80">{hint}</span>}
      </h4>
      {children}
    </div>
  );
}

function AudioRow({
  icon,
  asset,
  instructAs,
  productionId,
}: {
  icon: string;
  asset: AssetInfo;
  /** Asset-ref prefix when this row can be sent to the Director. */
  instructAs?: string;
  productionId?: string;
}) {
  return (
    <div className="flex items-center gap-2.5 py-1 text-xs">
      <span className="text-base leading-none">{icon}</span>
      <span className="w-28 shrink-0 truncate font-mono text-[11px] font-medium">{asset.shot_id}</span>
      <audio src={asset.path} controls className="h-7 flex-1" />
      {instructAs && (
        <InstructButton productionId={productionId} assetRef={`${instructAs}:${asset.shot_id}`} label={asset.shot_id} />
      )}
    </div>
  );
}

/** VO booth takes, dialogue, score and sound effects for the production. */
export default function AudioPanel() {
  const { t } = useTranslation('production');
  const { assets, productionId } = useProductionContext();

  const byName = (a: AssetInfo, b: AssetInfo) => a.shot_id.localeCompare(b.shot_id, undefined, { numeric: true });
  const vo = assets.filter((a) => a.type === 'vo').sort(byName);
  const booth = assets.filter((a) => a.type === 'vo_booth').sort(byName);
  const dialog = assets.filter((a) => a.type === 'dialog').sort(byName);
  const music = assets.filter((a) => a.type === 'music').sort(byName);
  const sfx = assets.filter((a) => a.type === 'sfx').sort(byName);

  const total = vo.length + booth.length + dialog.length + music.length + sfx.length;
  if (total === 0) {
    return (
      <EmptyState title={t('audio.emptyTitle')}>
        {t('audio.empty')} {t('audio.help')}
      </EmptyState>
    );
  }

  const counts = [
    booth.length && t('audio.countBooth', { count: booth.length }),
    dialog.length && `${dialog.length} ${t('audio.dialogue').toLowerCase()}`,
    vo.length && `${vo.length} ${t('audio.voiceover').toLowerCase()}`,
    music.length && `${music.length} ${t('audio.music').toLowerCase()}`,
    sfx.length && `${sfx.length} ${t('audio.sfx').toLowerCase()}`,
  ].filter(Boolean) as string[];

  return (
    <div>
      <SectionHeader title={t('step.audio')} help={t('audio.help')} subtitle={counts.join(' · ')} />

      <div className="space-y-5">
        {booth.length > 0 && (
          <Group title={t('audio.booth')} hint={t('audio.boothHint')}>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
              {booth.map((b) => (
                <div key={b.shot_id}>
                  <video
                    src={b.path}
                    className="aspect-video w-full rounded-lg border border-border bg-black"
                    playsInline
                    preload="metadata"
                    controls
                  />
                  <div className="truncate font-mono text-[9px] text-faint">{b.shot_id}</div>
                </div>
              ))}
            </div>
          </Group>
        )}

        {dialog.length > 0 && (
          <Group title={t('audio.dialogue')} hint={t('audio.dialogueHint')}>
            {dialog.map((d) => (
              <AudioRow key={d.shot_id} icon="💬" asset={d} instructAs="dialog" productionId={productionId} />
            ))}
          </Group>
        )}

        {vo.length > 0 && (
          <Group title={t('audio.voiceover')} hint={t('audio.voiceoverHint')}>
            {vo.map((v) => (
              <AudioRow key={v.shot_id} icon="📖" asset={v} />
            ))}
          </Group>
        )}

        {music.length > 0 && (
          <Group title={t('audio.music')}>
            {music.map((m) => (
              <AudioRow key={m.shot_id} icon="🎵" asset={m} instructAs="music" productionId={productionId} />
            ))}
          </Group>
        )}

        {sfx.length > 0 && (
          <Group title={t('audio.sfx')}>
            {sfx.map((s) => (
              <AudioRow key={s.shot_id} icon="🔊" asset={s} />
            ))}
          </Group>
        )}
      </div>
    </div>
  );
}
